import { useEffect, useState, useMemo } from 'react'
import {
  Conversation,
  subscribeToUserConversations,
} from '../services/messagingService'

export interface UnreadConversationsState {
  /** Total number of conversations with at least one unread message. */
  totalUnreadConversations: number
  /** Map of conversationId → unread message count for the current user. */
  perConversationUnread: Map<string, number>
  /** Raw conversations list (already filtered by role). Useful for callers
   *  that want to render a list and a badge from the same subscription. */
  conversations: Conversation[]
  /** True until the first snapshot arrives. */
  loading: boolean
}

/**
 * Subscribes to the current user's conversations and derives:
 *   1. A count of conversations with at least one unread message
 *      (the bell / nav badge value).
 *   2. A per-conversation unread count map (the per-row pill value).
 *
 * Uses the per-participant `unreadForTeacher` / `unreadForStudent`
 * counters on each conversation doc as the source of truth, with the
 * legacy single `unread` boolean as a backward-compat fallback for
 * docs that pre-date the counter rollout.
 *
 * Firestore deduplicates identical onSnapshot queries internally, so
 * mounting this hook in multiple places (sidebar + messages page) for
 * the same user does not multiply listener cost.
 */
export const useUnreadConversations = (
  userId: string | null | undefined,
  role: 'teacher' | 'student',
): UnreadConversationsState => {
  const [conversations, setConversations] = useState<Conversation[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (!userId) {
      setConversations([])
      setLoading(false)
      return
    }
    setLoading(true)
    const unsubscribe = subscribeToUserConversations(
      userId,
      (convs) => {
        setConversations(convs)
        setLoading(false)
      },
      role,
    )
    return () => {
      unsubscribe()
    }
  }, [userId, role])

  const { totalUnreadConversations, perConversationUnread } = useMemo(() => {
    const map = new Map<string, number>()
    let total = 0
    for (const conv of conversations) {
      // Skip soft-archived conversations on the viewer's side — they
      // shouldn't drive a badge the user can't see in their inbox.
      const archivedForMe =
        role === 'teacher'
          ? conv.archivedForTeacher === true
          : conv.archivedForStudent === true
      if (archivedForMe) continue

      const counter =
        role === 'teacher' ? conv.unreadForTeacher : conv.unreadForStudent
      let count = typeof counter === 'number' ? counter : 0

      // Backward compat: if the new counter is absent/zero but the
      // legacy `unread` boolean is true AND the last message was sent
      // by the *other* party, treat it as 1 unread. This keeps badges
      // working for conversations created before the counter rollout.
      if (count === 0 && conv.unread === true) {
        const senderIsMe = conv.lastMessageSenderId === userId
        if (!senderIsMe) {
          count = 1
        }
      }

      if (count > 0) {
        map.set(conv.id, count)
        total += 1
      }
    }
    return {
      totalUnreadConversations: total,
      perConversationUnread: map,
    }
  }, [conversations, role, userId])

  return {
    totalUnreadConversations,
    perConversationUnread,
    conversations,
    loading,
  }
}
